import React, { useCallback, useMemo } from 'react';
import {
  MeetingInsights,
  PresentationDeck,
  SlideDeckSummary,
  SlideTimelineEntry,
  SpeechSegment,
  VideoAnalysisMeta,
} from '../../../../services/api.service';
import { attachSlideThumbnails, buildVideoMoments } from '../../utils/video-moments.utils';
import {
  buildSectionSummaryCards,
  buildSlideSections,
  countMomentsByBadge,
} from '../../utils/slide-panel.utils';
import InsightsSidebar from './insights-sidebar.component';
import MomentCards from './moment-cards.component';
import PlayerSlidePanel from './player-slide-panel.component';
import SectionSummaryCards from './section-summary-cards.component';
import SyncedTranscript from './synced-transcript.component';
import styles from './video-analytics.module.scss';

type Props = {
  videoUrl: string;
  durationSec: number;
  currentTime: number;
  isPlaying?: boolean;
  segments: SpeechSegment[];
  insights: MeetingInsights | null;
  slideTimeline: SlideTimelineEntry[];
  presentationSlides: SlideTimelineEntry[];
  presentationDecks: PresentationDeck[];
  slideDeckSummary: SlideDeckSummary | null;
  videoAnalysis?: VideoAnalysisMeta | null;
  meetingId: string;
  getFrameUrl: (meetingId: string, index: number, crop?: boolean) => string;
  onTimeChange: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  onSeek: (seconds: number) => void;
  onTogglePlayPause?: () => void;
  seekRef?: React.MutableRefObject<((time: number) => void) | null>;
  togglePlayPauseRef?: React.MutableRefObject<(() => void) | null>;
  onSummarizeSlides?: () => Promise<void>;
  onGeneratePptx?: () => Promise<void>;
  summarizing?: boolean;
  generatingPptx?: boolean;
};

/**
 * VideoAnalytics — плеер+слайды, карточки моментов, суммаризация по разделам, транскрипт.
 */
const VideoAnalyticsHub: React.FC<Props> = ({
  videoUrl,
  durationSec,
  currentTime,
  isPlaying = false,
  segments,
  insights,
  slideTimeline,
  presentationSlides,
  presentationDecks,
  slideDeckSummary,
  videoAnalysis,
  meetingId,
  getFrameUrl,
  onTimeChange,
  onDurationChange,
  onPlayingChange,
  onSeek,
  onTogglePlayPause,
  seekRef,
  togglePlayPauseRef,
  onSummarizeSlides,
  onGeneratePptx,
  summarizing = false,
  generatingPptx = false,
}) => {
  const moments = useMemo(() => {
    const base = buildVideoMoments(insights);
    return attachSlideThumbnails(base, slideTimeline);
  }, [insights, slideTimeline]);

  const sections = useMemo(
    () => buildSlideSections(
      presentationDecks,
      presentationSlides,
      slideTimeline,
      slideDeckSummary,
      moments,
    ),
    [presentationDecks, presentationSlides, slideTimeline, slideDeckSummary, moments],
  );

  const sectionCards = useMemo(
    () => buildSectionSummaryCards(sections, slideDeckSummary, moments),
    [sections, slideDeckSummary, moments],
  );

  const eventCounts = useMemo(() => countMomentsByBadge(moments), [moments]);

  const getThumbUrl = useCallback((slideIndex: number) => {
    return getFrameUrl(meetingId, slideIndex, true);
  }, [getFrameUrl, meetingId]);

  const hasVlm = slideTimeline.length > 0 || sections.some(s => s.slides.length > 0);

  return (
    <div className={styles.hub}>
      <PlayerSlidePanel
        videoUrl={videoUrl}
        durationSec={durationSec}
        currentTime={currentTime}
        markers={moments}
        sections={sections}
        getThumbUrl={getThumbUrl}
        onTimeChange={onTimeChange}
        onDurationChange={onDurationChange}
        onPlayingChange={onPlayingChange}
        onSeek={onSeek}
        seekRef={seekRef}
        togglePlayPauseRef={togglePlayPauseRef}
      />

      <div className={styles.hubBody}>
        <div className={styles.hubMain}>
          <MomentCards
            moments={moments}
            getThumbUrl={getThumbUrl}
            onSeek={onSeek}
          />

          {(sectionCards.length > 0 || slideDeckSummary?.deck_summary) && (
            <SectionSummaryCards cards={sectionCards} onSeek={onSeek} />
          )}

          {segments.length > 0 && (
            <SyncedTranscript
              segments={segments}
              taggedReplicas={insights?.tagged_replicas}
              currentTime={currentTime}
              isPlaying={isPlaying}
              onSeek={onSeek}
              onTogglePlayPause={onTogglePlayPause}
            />
          )}
        </div>

        <InsightsSidebar
          durationSec={durationSec}
          slideDeckSummary={slideDeckSummary}
          eventCounts={eventCounts}
          framesAnalyzed={hasVlm ? (videoAnalysis?.frames_analyzed ?? slideTimeline.length) : undefined}
          slidesCount={hasVlm ? sections.reduce((n, s) => n + s.slides.length, 0) : undefined}
          onSummarizeSlides={hasVlm ? onSummarizeSlides : undefined}
          onGeneratePptx={hasVlm ? onGeneratePptx : undefined}
          summarizing={summarizing}
          generatingPptx={generatingPptx}
        />
      </div>
    </div>
  );
};

export default VideoAnalyticsHub;
